import React, { useEffect, useState } from "react";
import api from "../../api";
import { useTranslation } from "react-i18next";

import { Bar, Pie, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from "chart.js";

import {
  FiDollarSign,
  FiTrendingUp,
  FiFileText,
  FiClock,
  FiPhoneCall,
  FiCheckCircle,
  FiUser,
  FiBriefcase,
  FiCreditCard,
  FiTrendingDown,
  FiTruck,
} from "react-icons/fi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

const PERIODS = [
  { key: "30",  label: "Last 30 days",   days: 30 },
  { key: "90",  label: "Last 90 days",   days: 90 },
  { key: "365", label: "Last 12 months", days: 365 },
];

const euro = (n) =>
  `${(Number(n) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;

const HomeDashboard = () => {
  const { t } = useTranslation();

  const [stats,   setStats]   = useState(null);
  const [period,  setPeriod]  = useState("365");
  const [loading, setLoading] = useState(true);

  // Toutes les stats viennent d'un endpoint d'agrégation SQL (source de vérité,
  // plus de calcul côté client sur des données paginées).
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const days = PERIODS.find((p) => p.key === period)?.days || 365;
    api.get(`/dashboard/stats?days=${days}`)
      .then((res) => { if (mounted) setStats(res.data); })
      .catch(() => { if (mounted) setStats(null); })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [period]);

  const kpi = stats?.kpi || {};
  const totalRevenue   = kpi.revenue        || 0;
  const totalProfit    = kpi.profit         || 0;
  const totalMissions  = kpi.missions       || 0;
  const totalSalaries  = kpi.salaries       || 0;
  const totalCharges   = kpi.charges        || 0;
  const unpaidSalaries = kpi.unpaidSalaries || 0;
  const qPending       = kpi.quotesPending  || 0;
  const qFollowUp      = kpi.quotesFollowUp || 0;
  const qValidated     = kpi.quotesValidated || 0;
  const qCanceled      = kpi.quotesCanceled || 0;
  const qSubcontracted = kpi.quotesSubcontracted || 0;
  const conversion     = kpi.conversion     || 0;
  const investmentNet  = kpi.investmentNet  || 0;

  // Séries mensuelles (YYYY-MM → "Aug 2025")
  const fmtMonth = (ym) => {
    const [y, m] = (ym || "").split("-");
    if (!y || !m) return ym;
    return new Date(Number(y), Number(m) - 1, 1)
      .toLocaleString("en-US", { month: "short", year: "numeric" });
  };
  const monthLabels   = (stats?.series?.months || []).map(fmtMonth);
  const revenueSeries = stats?.series?.revenue || [];
  const profitSeries  = stats?.series?.profit  || [];

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 0, font: { size: 11 }, color: "#94a3b8" },
      },
      y: {
        grid: { color: "#f1f5f9" },
        ticks: { callback: (v) => `${v}€`, font: { size: 11 }, color: "#94a3b8" },
        border: { display: false },
      },
    },
  };

  const revenueBarData = {
    labels: monthLabels,
    datasets: [{
      label: t("dashboard.monthlyRevenue"),
      data: revenueSeries,
      backgroundColor: "#fb923c",
      hoverBackgroundColor: "#f97316",
      borderRadius: 6,
      borderSkipped: false,
    }],
  };

  const profitLineData = {
    labels: monthLabels,
    datasets: [{
      label: t("dashboard.monthlyProfit"),
      data: profitSeries,
      fill: true,
      borderColor: "#f97316",
      backgroundColor: "rgba(249,115,22,0.06)",
      tension: 0.4,
      pointRadius: 4,
      pointBackgroundColor: "#f97316",
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
    }],
  };

  const quotesPieData = {
    labels: [
      t("dashboard.quotesPending"),
      t("dashboard.quotesFollowUp"),
      t("dashboard.quotesSigned"),
      t("dashboard.quotesCanceled"),
    ],
    datasets: [{
      data: [qPending, qFollowUp, qValidated, qCanceled],
      backgroundColor: ["#fbbf24", "#38bdf8", "#34d399", "#f87171"],
      borderWidth: 0,
      hoverOffset: 6,
    }],
  };

  const periodLabel = PERIODS.find(p => p.key === period)?.label || "";

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="page-title">{t("dashboard.title")}</h1>
          <p className="page-subtitle">
            {t("dashboard.subtitle", "Vue d'ensemble de l'activité et des finances")}
          </p>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="select select-bordered select-sm w-full sm:w-auto bg-base-100 border-base-200 text-base-content/80 font-medium"
        >
          {PERIODS.map((p) => (
            <option key={p.key} value={p.key}>
              {t(`dashboard.period.${p.key}`, p.label)}
            </option>
          ))}
        </select>
      </div>

      {/* ── KPI grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        <KpiCard
          icon={<FiDollarSign />}
          label={t("dashboard.revenuePeriod")}
          value={euro(totalRevenue)}
          accent="orange"
        />
        <KpiCard
          icon={<FiTrendingUp />}
          label={t("dashboard.profitPeriod")}
          value={euro(totalProfit)}
          accent="emerald"
        />
        <KpiCard
          icon={<FiFileText />}
          label={t("dashboard.quotesSigned")}
          value={`${qValidated}`}
          sub={`${conversion}% conversion`}
          accent="indigo"
        />
        <KpiCard
          icon={<FiClock />}
          label={t("dashboard.quotesPending")}
          value={`${qPending}`}
          accent="amber"
        />
        <KpiCard
          icon={<FiPhoneCall />}
          label={t("dashboard.quotesFollowUp")}
          value={`${qFollowUp}`}
          accent="sky"
        />
        <KpiCard
          icon={<FiTruck />}
          label={t("dashboard.quotesSubcontracted", "Devis sous-traités")}
          value={`${qSubcontracted}`}
          accent="orange"
        />
        <KpiCard
          icon={<FiCheckCircle />}
          label={t("dashboard.missions")}
          value={`${totalMissions}`}
          accent="orange"
        />
        <KpiCard
          icon={<FiBriefcase />}
          label={t("dashboard.salaries")}
          value={euro(totalSalaries)}
          accent="amber"
        />
        <KpiCard
          icon={<FiCreditCard />}
          label={t("dashboard.charges")}
          value={euro(totalCharges)}
          accent="rose"
        />
        <KpiCard
          icon={<FiUser />}
          label={t("dashboard.unpaidSalaries")}
          value={euro(unpaidSalaries)}
          accent={unpaidSalaries > 0 ? "rose" : "emerald"}
        />
        <KpiCard
          icon={investmentNet >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
          label={t("dashboard.investmentsNet", "Net investissements")}
          value={euro(investmentNet)}
          accent="indigo"
        />
      </div>

      {/* ── Charts ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title={t("dashboard.revenueByMonth")} sub={periodLabel}>
          <div className="h-56">
            <Bar data={revenueBarData} options={chartOptions} />
          </div>
        </ChartCard>

        <ChartCard title={t("dashboard.quotesBreakdown")} sub={periodLabel}>
          <div className="h-56">
            {qPending + qFollowUp + qValidated + qCanceled === 0 ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 text-base-content/30">
                <div className="w-14 h-14 rounded-full border-4 border-dashed border-base-300" />
                <span className="text-xs">{t("dashboard.noQuotesInPeriod", "Aucun devis sur la période")}</span>
              </div>
            ) : (
              <Pie
                data={quotesPieData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: { boxWidth: 10, padding: 14, font: { size: 12 }, color: "#64748b" },
                    },
                  },
                }}
              />
            )}
          </div>
        </ChartCard>

        <ChartCard className="xl:col-span-2" title={t("dashboard.profitByMonth")} sub={periodLabel}>
          <div className="h-56">
            <Line data={profitLineData} options={chartOptions} />
          </div>
        </ChartCard>
      </div>

      {loading && (
        <div className="flex justify-center py-6">
          <div className="flex items-center gap-2 text-sm text-base-content/50">
            <span className="loading loading-spinner loading-sm text-primary" />
            Chargement…
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeDashboard;

/* ─────────────────────────────── */
/* KPI card                        */
/* ─────────────────────────────── */

const ACCENT = {
  orange:  { bg: "bg-orange-500/10",  icon: "bg-orange-500/15 text-orange-500",  border: "border-orange-500/20" },
  emerald: { bg: "bg-emerald-500/10", icon: "bg-emerald-500/15 text-emerald-500", border: "border-emerald-500/20" },
  indigo:  { bg: "bg-indigo-500/10",  icon: "bg-indigo-500/15 text-indigo-500",  border: "border-indigo-500/20" },
  amber:   { bg: "bg-amber-500/10",   icon: "bg-amber-500/15 text-amber-500",    border: "border-amber-500/20" },
  sky:     { bg: "bg-sky-500/10",     icon: "bg-sky-500/15 text-sky-500",        border: "border-sky-500/20" },
  rose:    { bg: "bg-rose-500/10",    icon: "bg-rose-500/15 text-rose-500",      border: "border-rose-500/20" },
};

function KpiCard({ icon, label, value, accent = "orange", sub }) {
  const a = ACCENT[accent] || ACCENT.orange;
  return (
    <div className={`rounded-xl border ${a.border} ${a.bg} p-4 flex flex-col gap-2.5 hover:shadow-sm transition-shadow`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold tracking-wider text-base-content/50 uppercase truncate pr-2">
          {label}
        </span>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm shrink-0 ${a.icon}`}>
          {icon}
        </div>
      </div>
      <div className="stat-number">{value}</div>
      {sub && <div className="text-xs text-base-content/50 -mt-1">{sub}</div>}
    </div>
  );
}

/* ─────────────────────────────── */
/* Chart card                      */
/* ─────────────────────────────── */

function ChartCard({ title, sub, children, className = "" }) {
  return (
    <div className={`bg-base-100 rounded-xl border border-base-200 shadow-sm overflow-hidden ${className}`}>
      <div className="px-5 py-4 border-b border-base-300 flex items-baseline gap-2">
        <h3 className="text-sm font-semibold text-base-content">{title}</h3>
        {sub && <span className="text-xs text-base-content/50">{sub}</span>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
